import React, { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Search, Hash, ArrowLeft, ChevronRight, Lock, Trophy } from "lucide-react";

interface UserRegistrationProps {
  onRegistrationComplete: (userData: { 
    id: string;
    name: string; 
    team: string;
    playerId: string;
    jerseyNumber: number;
    skillLevel?: string;
  }) => void;
}

interface Player {
  id: string;
  full_name: string;
  team: string;
  jersey_number: number;
  skill_level: string;
}

export const UserRegistration: React.FC<UserRegistrationProps> = ({ 
  onRegistrationComplete 
}) => {
  const [name, setName] = useState("");
  const [jerseyNumber, setJerseyNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [playerMatch, setPlayerMatch] = useState<Player | null>(null);
  const [allPlayers, setAllPlayers] = useState<Player[]>([]);
  const [filteredPlayers, setFilteredPlayers] = useState<Player[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [nameFlash, setNameFlash] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const successAudioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    successAudioRef.current = new Audio('/audio/success.mp3');
    successAudioRef.current.volume = 0.5;
  }, []);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const { data: players, error } = await supabase
          .from('players')
          .select('id, full_name, team, jersey_number, skill_level')
          .eq('is_active', true)
          .order('full_name');
        if (error) throw error;
        setAllPlayers(players || []);
      } catch (error) {
        console.error('Error fetching players:', error);
      }
    };
    fetchPlayers();
  }, []);

  useEffect(() => {
    if (name.trim().length >= 1) {
      const searchTerm = name.trim().toLowerCase();
      const filtered = allPlayers.filter(player =>
        player.full_name.toLowerCase().includes(searchTerm) ||
        player.full_name.toLowerCase().split(' ').some(namePart => 
          namePart.startsWith(searchTerm)
        )
      );
      filtered.sort((a, b) => {
        const aStartsWith = a.full_name.toLowerCase().startsWith(searchTerm);
        const bStartsWith = b.full_name.toLowerCase().startsWith(searchTerm);
        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;
        return a.full_name.localeCompare(b.full_name);
      });
      setFilteredPlayers(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setFilteredPlayers([]);
      setShowSuggestions(false);
    }
  }, [name, allPlayers]);

  const handleNameSelect = (selectedPlayer: Player) => {
    // Flash effect
    setNameFlash(true);
    setTimeout(() => setNameFlash(false), 600);
    
    // Play selection sound
    try { successAudioRef.current?.play(); } catch {}

    setName(selectedPlayer.full_name);
    setShowSuggestions(false);
    setPlayerMatch(selectedPlayer);
    
    // Brief delay for the flash effect before transitioning
    setTimeout(() => setStep(2), 400);
  };

  const handleNameSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast({ title: "Enter your name", description: "Type your name to continue.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { data: players, error } = await supabase
        .from('players')
        .select('id, full_name, team, jersey_number, skill_level')
        .eq('is_active', true)
        .ilike('full_name', name.trim());
      if (error) throw error;
      const exactMatch = players?.find(player => 
        player.full_name.toLowerCase() === name.trim().toLowerCase()
      );
      if (exactMatch) {
        handleNameSelect(exactMatch);
      } else {
        toast({ title: "Player not found", description: "Select your name from the dropdown list.", variant: "destructive" });
      }
    } catch (error) {
      console.error('Name verification error:', error);
      toast({ title: "Verification Error", description: "Unable to verify. Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleJerseySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!jerseyNumber.trim()) {
      toast({ title: "Jersey number required", description: "Enter the number on your shirt.", variant: "destructive" });
      return;
    }
    if (!playerMatch) {
      toast({ title: "Error", description: "Player not found. Please start over.", variant: "destructive" });
      setStep(1);
      return;
    }
    const enteredNumber = parseInt(jerseyNumber.trim());
    if (isNaN(enteredNumber)) {
      toast({ title: "Invalid number", description: "Enter a valid jersey number.", variant: "destructive" });
      return;
    }

    const { data: possibleMatches, error: checkError } = await supabase
      .from('players')
      .select('*')
      .eq('full_name', playerMatch.full_name)
      .eq('team', playerMatch.team)
      .eq('jersey_number', enteredNumber)
      .eq('is_active', true);

    if (checkError) {
      toast({ title: "Error", description: "Unable to verify. Try again.", variant: "destructive" });
      return;
    }
    if (!possibleMatches || possibleMatches.length === 0) {
      toast({ title: "Access Denied", description: "Invalid jersey number.", variant: "destructive" });
      return;
    }

    const finalPlayerMatch = possibleMatches[0];
    setLoading(true);
    try {
      const { data: profileData, error: profileError } = await supabase
        .rpc('get_or_create_profile', {
          p_player_id: finalPlayerMatch.id,
          p_name: finalPlayerMatch.full_name,
          p_team: finalPlayerMatch.team
        });
      if (profileError) {
        toast({ title: "Profile Error", description: "Unable to set up profile. Try again.", variant: "destructive" });
        return;
      }

      // Success transition
      setIsTransitioning(true);
      try { successAudioRef.current?.play(); } catch {}

      const userData = {
        id: profileData || '',
        name: finalPlayerMatch.full_name,
        team: finalPlayerMatch.team,
        playerId: finalPlayerMatch.id,
        jerseyNumber: finalPlayerMatch.jersey_number,
        skillLevel: finalPlayerMatch.skill_level
      };

      // Delay for the reveal transition
      setTimeout(() => {
        onRegistrationComplete(userData);
        toast({ title: "Welcome!", description: `Let's go, ${playerMatch.full_name}!` });
      }, 800);
    } catch (error) {
      console.error('Registration error:', error);
      setIsTransitioning(false);
      toast({ title: "Error", description: "Unable to complete sign in.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToName = () => {
    setStep(1);
    setPlayerMatch(null);
    setJerseyNumber("");
  };

  const isTyping = name.length > 0;

  return (
    <div className={`w-full transition-all duration-700 ${isTransitioning ? 'scale-105 opacity-0 blur-sm' : ''}`}>
      {/* Progress Pips */}
      <div className="flex items-center justify-center gap-2.5 mb-6">
        <span className={`block h-1.5 rounded-full transition-all duration-500 ${step >= 1 ? 'w-10 bg-gradient-to-r from-amber-400 to-orange-500 shadow-[0_0_8px_rgba(251,146,60,0.5)]' : 'w-5 bg-white/15'}`} />
        <span className={`block h-1.5 rounded-full transition-all duration-500 ${step >= 2 ? 'w-10 bg-gradient-to-r from-amber-400 to-orange-500 shadow-[0_0_8px_rgba(251,146,60,0.5)]' : 'w-5 bg-white/15'}`} />
      </div>

      {/* Draft Card Container */}
      <div className={`relative bg-white/[0.04] backdrop-blur-2xl rounded-2xl border transition-all duration-500 p-6 md:p-8 ${
        isTyping && step === 1
          ? 'border-amber-400/30 shadow-[0_0_30px_rgba(251,146,60,0.12)]' 
          : 'border-white/8 shadow-2xl'
      }`}>
        {/* Scan line animation when typing */}
        {isTyping && step === 1 && (
          <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
            <div className="absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-400/40 to-transparent animate-[scanline_2s_ease-in-out_infinite]" 
              style={{ top: '30%' }} />
          </div>
        )}

        {/* Locked Trophy Teaser */}
        <div className="flex items-center justify-center mb-5">
          <div className="relative">
            <div className="w-12 h-12 rounded-full bg-white/[0.06] border border-white/10 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-white/25" />
            </div>
            <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-zinc-800 border border-white/10 flex items-center justify-center">
              <Lock className="w-2.5 h-2.5 text-white/30" />
            </div>
          </div>
        </div>

        {/* Header — Athletic Bold Italic */}
        <div className="text-center mb-6">
          <h3 className="text-xl font-black italic text-white tracking-wide uppercase">
            {step === 1 ? "PLAYER'S SIGN IN" : "VERIFY IDENTITY"}
          </h3>
          <p className="text-sm text-white/50 mt-1.5 font-light">
            {step === 1 ? "Enter your name to begin your journey" : "Confirm your jersey number"}
          </p>
        </div>

        {step === 1 ? (
          <form onSubmit={handleNameSubmit} className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
              <Input
                type="text"
                placeholder="Search your name..."
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={`pl-10 h-12 bg-white/[0.03] border-white/10 text-white placeholder:text-white/25 rounded-xl focus:border-amber-400/60 focus:ring-1 focus:ring-amber-400/30 text-sm font-medium transition-all duration-300 ${
                  nameFlash ? 'text-amber-400 border-amber-400/50 shadow-[0_0_20px_rgba(251,146,60,0.2)]' : ''
                }`}
                disabled={loading}
                autoComplete="off"
              />

              {/* Glassmorphism Dropdown */}
              {showSuggestions && filteredPlayers.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1.5 bg-black/80 backdrop-blur-2xl border border-white/10 rounded-xl shadow-[0_20px_60px_rgba(0,0,0,0.5)] z-50 max-h-56 overflow-y-auto">
                  {filteredPlayers.slice(0, 8).map((player) => (
                    <button
                      key={player.id}
                      type="button"
                      onClick={() => handleNameSelect(player)}
                      className="w-full px-4 py-3 text-left hover:bg-amber-400/10 active:bg-amber-400/15 transition-all duration-150 group flex items-center justify-between border-b border-white/[0.04] last:border-b-0"
                    >
                      <div>
                        <span className="text-white text-sm font-semibold block group-hover:text-amber-300 transition-colors">
                          {player.full_name}
                        </span>
                        <span className="text-white/35 text-xs">
                          {player.team}
                        </span>
                      </div>
                      <ChevronRight className="h-3.5 w-3.5 text-white/15 group-hover:text-amber-400 transition-colors" />
                    </button>
                  ))}
                  {filteredPlayers.length > 8 && (
                    <div className="px-4 py-2 text-center text-white/25 text-xs">
                      {filteredPlayers.length - 8} more — keep typing
                    </div>
                  )}
                </div>
              )}
            </div>

            <p className="text-xs text-white/30 text-center">
              {name.length > 0 && filteredPlayers.length === 0 && name.length >= 2 
                ? "No matches found — try a different spelling"
                : "Type a few letters and select from the list"
              }
            </p>

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-foreground hover:from-amber-500 hover:to-orange-600 font-bold rounded-full h-12 text-sm shadow-lg shadow-amber-500/20 active:scale-[0.98] uppercase tracking-widest" 
              disabled={loading || !name.trim()}
            >
              {loading ? "Searching..." : "FIND MY NAME"}
            </Button>
          </form>
        ) : (
          <form onSubmit={handleJerseySubmit} className="space-y-4">
            {/* Player ID confirmation card */}
            <div className="p-4 bg-gradient-to-br from-white/[0.06] to-white/[0.02] rounded-xl border border-white/10 text-center">
              <p className="text-amber-400 font-black italic text-lg uppercase tracking-wide">{playerMatch?.full_name}</p>
              <p className="text-white/40 text-xs mt-0.5 uppercase tracking-wider">{playerMatch?.team}</p>
            </div>
            
            <div className="relative">
              <Hash className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
              <Input
                type="number"
                placeholder="Jersey number"
                value={jerseyNumber}
                onChange={(e) => setJerseyNumber(e.target.value)}
                className="pl-10 h-12 bg-white/[0.03] border-white/10 text-white placeholder:text-white/25 rounded-xl focus:border-amber-400/60 focus:ring-1 focus:ring-amber-400/30 text-sm font-medium text-center"
                disabled={loading}
                min="1"
              />
            </div>

            <p className="text-xs text-white/30 text-center">
              Enter the number on your shirt to verify
            </p>

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-amber-400 to-orange-500 text-foreground hover:from-amber-500 hover:to-orange-600 font-bold rounded-full h-12 text-sm shadow-lg shadow-amber-500/20 active:scale-[0.98] uppercase tracking-widest" 
              disabled={loading || !jerseyNumber.trim()}
            >
              {loading ? "Verifying..." : "ENTER THE LAB"}
            </Button>
            
            <Button 
              type="button" 
              variant="ghost"
              className="w-full text-white/40 hover:text-white hover:bg-white/5 rounded-full h-10 text-sm" 
              onClick={handleBackToName}
              disabled={loading}
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              Back
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};
